import React, { useMemo } from 'react';
import cx from 'classnames';
import { Button, Text, H4, useIntl, Image, Link } from 'platform-components';
import { UI_KIT_TEXT_TYPOGRAPHY, UI_KIT_HEADER_TYPOGRAPHY } from 'platform-components/constants';
import { useLocation } from 'react-router';
import ym from 'react-yandex-metrika';
import { getTeacherDrilledQueryParams } from 'utils/getTeacherDrilledQueryParams';

import './library-banner.scss';

export enum LIBRARY_BANNER_THEME {
    ONE_COLUMN = 'one_column',
    TWO_COLUMN = 'two_column',
    THREE_COLUMN = 'three_column',
}

export interface OwnProps {
    backgroundColor?: string;
    buttonText?: string;
    buttonUrl: string;
    desc?: string;
    imageUrl?: string;
    imageMix?: string;
    isAbsoluteLink?: boolean;
    metrika?: {
        goal_name?: string;
        goal_params: object;
    };
    textWrapperMix?: string;
    title?: string;
}

export interface Props extends OwnProps {}

const LibraryBanner = React.memo((props: Props) => {
    const {
        backgroundColor,
        buttonText,
        buttonUrl,
        imageUrl,
        imageMix,
        isAbsoluteLink,
        desc,
        metrika,
        textWrapperMix,
        title,
    } = props;
    const intl = useIntl();

    const { search } = useLocation();

    const sendYandexMetrika = () =>
        ym('reachGoal', metrika?.goal_name || 'teacher_vitrina_click_library_banner', metrika?.goal_params);

    const buttonTxt = buttonText || intl.formatMessage({ id: 'showcase.libraryBanner.buttonText' });

    const buttonUrlWithQuery = useMemo(() => {
        const queryParams = getTeacherDrilledQueryParams(search);
        return `${buttonUrl}${buttonUrl.includes('?') ? '&' : '?'}${queryParams.toString()}`;
    }, [buttonUrl, search]);

    return (
        <Link
            className="library-banner"
            isRouter={!isAbsoluteLink}
            onClick={sendYandexMetrika}
            style={{ backgroundColor }}
            to={buttonUrlWithQuery}
        >
            <div className={cx('library-banner__text-wrapper', textWrapperMix)}>
                {title && (
                    <div className="library-banner__title">
                        <H4 typography={UI_KIT_HEADER_TYPOGRAPHY.headlineMMedium}>{title}</H4>
                    </div>
                )}
                {desc && (
                    <div className="library-banner__description">
                        <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLRegular}>{desc}</Text>
                    </div>
                )}
                <Button
                    className="library-banner__button"
                    transparencyTheme={Button.TRANSPARENCY_THEME.DARK}
                    view={Button.VIEW.PSEUDO}
                >
                    {buttonTxt}
                </Button>
            </div>
            {imageUrl && (
                <div className="library-banner__image-wrapper">
                    <Image alt="" className={cx('library-banner__image', imageMix)} src={imageUrl} />
                </div>
            )}
        </Link>
    );
});

export default LibraryBanner;
